import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { NativeSelect } from "@/components/ui/native-select";
import {
  Plus, Calendar, Search, Edit, Trash2, ChevronUp, ChevronDown, CheckCircle2,
  AlertTriangle, Clock, RefreshCw, FileDown, FileText, History, X, Loader2,
  LayoutDashboard, Copy, Save, Sparkles, Info,
} from "lucide-react";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { extractErrorMessage } from "@/lib/errorMessage";
import { gerarPDFEtapa, gerarPDFCompleto } from "@/lib/cronogramaPDF";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_ITEM_OPTS = [
  { value: "pendente", label: "Pendente" },
  { value: "em_andamento", label: "Em Andamento" },
  { value: "concluido", label: "Concluído" },
  { value: "atrasado", label: "Atrasado" },
];

const STATUS_ETAPA_COLOR = {
  pendente: "bg-amber-100 text-amber-800 border-amber-200",
  em_andamento: "bg-blue-100 text-blue-800 border-blue-200",
  concluido: "bg-emerald-100 text-emerald-800 border-emerald-200",
  atrasado: "bg-red-100 text-red-800 border-red-200",
};

const STATUS_LABEL = {
  pendente: "Pendente",
  em_andamento: "Em Andamento",
  concluido: "Concluído",
  atrasado: "Atrasado",
};

const TIPO_IMPREVISTO_OPTS = [
  { value: "chuva", label: "Chuva" },
  { value: "falta_colaborador", label: "Falta de colaborador" },
  { value: "atraso_material", label: "Atraso de material" },
  { value: "problema_estrutural", label: "Problema estrutural" },
  { value: "interdicao", label: "Interdição" },
  { value: "greve", label: "Greve" },
  { value: "acidente", label: "Acidente de trabalho" },
  { value: "outro", label: "Outro" },
];

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s + "T00:00:00").toLocaleDateString("pt-BR");
  } catch {
    return s;
  }
};

export default function CronogramasPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = sessionStorage.getItem("luvcorp_token");
  const headers = { Authorization: `Bearer ${token}` };

  const [cronogramas, setCronogramas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // currently opened cronograma dashboard
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/cronogramas`, { headers });
      setCronogramas(res.data || []);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao carregar cronogramas"));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Deep-link: open specific cronograma via ?openId= (from executive dashboard)
  useEffect(() => {
    const openId = searchParams.get("openId");
    if (openId && cronogramas.length > 0 && !selected) {
      const cron = cronogramas.find(c => c.id === openId);
      if (cron) {
        openDashboard(cron);
        // Remove param so subsequent navigations don't re-trigger
        searchParams.delete("openId");
        setSearchParams(searchParams, { replace: true });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cronogramas, searchParams]);

  const handleDeleteCronograma = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API}/cronogramas/${confirmDelete.id}`, { headers });
      toast.success("Cronograma excluído");
      setConfirmDelete(null);
      if (selected?.cronograma?.id === confirmDelete.id) setSelected(null);
      fetchList();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao excluir"));
    }
  };

  const openDashboard = async (cron) => {
    try {
      const res = await axios.get(`${API}/cronogramas/${cron.id}`, { headers });
      setSelected(res.data);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao abrir cronograma"));
    }
  };

  const refreshDashboard = useCallback(async () => {
    if (!selected?.cronograma?.id) return;
    try {
      const res = await axios.get(`${API}/cronogramas/${selected.cronograma.id}`, { headers });
      setSelected(res.data);
    } catch (err) {
      console.error(err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.cronograma?.id]);

  if (selected) {
    return (
      <MainLayout>
        <CronogramaDashboard
          data={selected}
          onClose={() => setSelected(null)}
          onRefresh={refreshDashboard}
          onDelete={() => setConfirmDelete(selected.cronograma)}
          headers={headers}
        />
        <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir cronograma</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza? Esta ação não pode ser desfeita. Todos os dados de etapas, itens e imprevistos serão perdidos.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteCronograma}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="btn-confirm-delete-cronograma"
              >
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-luvcorp-navy" data-testid="cronogramas-title">
              Cronogramas de Obra
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Planeje, monitore e replaneje a execução das obras com gráfico de Gantt e caminho crítico.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate("/operacional/cronogramas-dashboard")}
              data-testid="btn-dashboard-executivo"
            >
              <LayoutDashboard className="h-4 w-4 mr-1" /> Dashboard Executivo
            </Button>
            <Button
              onClick={() => setShowNewDialog(true)}
              className="bg-luvcorp-navy hover:bg-luvcorp-navy/90 text-white"
              data-testid="btn-novo-cronograma"
            >
              <Plus className="h-4 w-4 mr-1" /> Novo Cronograma
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Carregando...
          </div>
        ) : cronogramas.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center">
              <Calendar className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 mb-4">Nenhum cronograma cadastrado.</p>
              <Button onClick={() => setShowNewDialog(true)} variant="outline" data-testid="btn-novo-cronograma-empty">
                <Plus className="h-4 w-4 mr-1" /> Criar primeiro cronograma
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cronogramas.map((c) => (
              <Card
                key={c.id}
                className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => openDashboard(c)}
                data-testid={`cronograma-card-${c.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base text-luvcorp-navy truncate">
                      {c.contrato_titulo || "Contrato sem título"}
                    </CardTitle>
                    <Badge variant={c.status === "finalizado" ? "default" : "outline"} className="shrink-0">
                      {c.status === "finalizado" ? "Finalizado" : "Rascunho"}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500 truncate">{c.cliente_nome || "—"}</p>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>Início</span><span>{fmtDate(c.data_inicio)}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>Conclusão prevista</span><span>{fmtDate(c.data_conclusao_prevista)}</span>
                  </div>
                  {c.dias_perdidos_acumulados > 0 && (
                    <div className="flex items-center justify-between text-xs text-red-600">
                      <span className="flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Dias perdidos</span>
                      <span className="font-semibold">{c.dias_perdidos_acumulados}d</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-xs text-slate-700 pt-1 border-t">
                    <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {c.total_etapas} etapa{c.total_etapas !== 1 ? "s" : ""}</span>
                    <span className="text-luvcorp-navy font-medium">{c.duracao_prevista_dias} dias</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <NewCronogramaDialog
        open={showNewDialog}
        onClose={() => setShowNewDialog(false)}
        onCreated={(c) => { setShowNewDialog(false); fetchList(); openDashboard(c); }}
        headers={headers}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir cronograma</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza? Esta ação não pode ser desfeita. Todos os dados de etapas, itens e imprevistos serão perdidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCronograma}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="btn-confirm-delete-cronograma"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainLayout>
  );
}

// ==================== NEW CRONOGRAMA DIALOG ====================
function NewCronogramaDialog({ open, onClose, onCreated, headers }) {
  const [busca, setBusca] = useState("");
  const [contratos, setContratos] = useState([]);
  const [loadingContratos, setLoadingContratos] = useState(false);
  const [contrato, setContrato] = useState(null);
  const [incluirRascunhos, setIncluirRascunhos] = useState(false);
  const [duracao, setDuracao] = useState("");
  const [dataInicio, setDataInicio] = useState(new Date().toISOString().split("T")[0]);
  const [creating, setCreating] = useState(false);

  const fetchContratos = useCallback(async (term = "") => {
    setLoadingContratos(true);
    try {
      const params = new URLSearchParams();
      if (term) params.append("search", term);
      if (incluirRascunhos) params.append("incluir_rascunhos", "true");
      const res = await axios.get(`${API}/cronogramas-lookup/contratos-disponiveis?${params}`, { headers });
      setContratos(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error(extractErrorMessage(err, "Erro ao carregar contratos"));
      setContratos([]);
    } finally {
      setLoadingContratos(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incluirRascunhos]);

  // Initial load when dialog opens — DO NOT auto-fetch; only show after user types
  useEffect(() => {
    if (open) {
      setBusca("");
      setContrato(null);
      setDuracao("");
      setDataInicio(new Date().toISOString().split("T")[0]);
      setContratos([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Debounced search — só busca quando usuário digitou algo
  useEffect(() => {
    if (!open) return;
    const term = busca.trim();
    if (term.length < 2) {
      setContratos([]);
      return;
    }
    const t = setTimeout(() => fetchContratos(term), 300);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca, open, incluirRascunhos]);

  const dataConclusao = (() => {
    if (!dataInicio || !duracao) return "";
    const d = new Date(dataInicio + "T00:00:00");
    d.setDate(d.getDate() + parseInt(duracao, 10));
    return d.toISOString().split("T")[0];
  })();

  const handleCreate = async () => {
    if (!contrato) { toast.error("Selecione um contrato da lista"); return; }
    if (contrato.ja_tem_cronograma) { toast.error("Este contrato já possui cronograma."); return; }
    if (!duracao || parseInt(duracao, 10) < 1 || parseInt(duracao, 10) > 1095) {
      toast.error("Duração deve estar entre 1 e 1095 dias"); return;
    }
    if (!dataInicio) { toast.error("Informe a data de início"); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/cronogramas`, {
        contrato_id: contrato.contrato_id,
        duracao_prevista_dias: parseInt(duracao, 10),
        data_inicio: dataInicio,
      }, { headers });
      onCreated(res.data);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao criar cronograma"));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="dialog-novo-cronograma">
        <DialogHeader>
          <DialogTitle>Novo Cronograma</DialogTitle>
          <DialogDescription>Selecione um contrato ativo e defina prazo e início da obra.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2 overflow-y-auto flex-1">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Buscar Contrato * <span className="text-slate-400 font-normal">(por nome do cliente, nº ou título)</span></Label>
              <label className="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer">
                <input
                  type="checkbox"
                  checked={incluirRascunhos}
                  onChange={(e) => setIncluirRascunhos(e.target.checked)}
                  className="accent-luvcorp-navy"
                  data-testid="checkbox-incluir-rascunhos"
                />
                Incluir rascunhos
              </label>
            </div>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <Input
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Digite o nome do cliente (mín. 2 letras)..."
                className="pl-9"
                data-testid="input-busca-contrato"
                autoFocus
              />
            </div>
          </div>

          {busca.trim().length >= 2 && (
          <div className="border border-slate-200 rounded-lg max-h-64 overflow-y-auto" data-testid="contratos-list">
            {loadingContratos ? (
              <div className="p-6 text-center text-xs text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin inline mr-1.5" /> Buscando contratos...
              </div>
            ) : contratos.length === 0 ? (
              <div className="p-6 text-center text-xs text-slate-500" data-testid="contratos-empty">
                Nenhum contrato encontrado para &quot;{busca}&quot;.
              </div>
            ) : (
              contratos.map((c) => {
                const selected = contrato?.contrato_id === c.contrato_id;
                const disabled = c.ja_tem_cronograma;
                return (
                  <button
                    key={c.contrato_id}
                    type="button"
                    onClick={() => !disabled && setContrato(c)}
                    disabled={disabled}
                    className={`w-full text-left px-3 py-2.5 border-b last:border-b-0 transition-colors ${
                      selected ? "bg-emerald-50 border-l-4 border-l-emerald-500" :
                      disabled ? "opacity-50 cursor-not-allowed bg-slate-50" :
                      "hover:bg-slate-50"
                    }`}
                    data-testid={`contrato-option-${c.contrato_id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-slate-800 truncate">{c.titulo || c.numero_contrato || "Sem título"}</span>
                          {c.numero_contrato && c.numero_contrato !== c.titulo && (
                            <span className="text-xs text-slate-400 font-mono">#{c.numero_contrato}</span>
                          )}
                          <Badge variant="outline" className={`text-[9px] ${c.status === "ativo" ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-300"}`}>
                            {c.status}
                          </Badge>
                          {disabled && (
                            <Badge variant="outline" className="text-[9px] border-amber-300 bg-amber-50 text-amber-700">
                              Já tem cronograma
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 truncate">
                          {c.nome_cliente || "Sem cliente"} {c.endereco_obra ? ` · ${c.endereco_obra}` : ""}
                        </div>
                        {c.valor_total > 0 && (
                          <div className="text-[10px] text-slate-400 mt-0.5 font-mono">
                            R$ {Number(c.valor_total).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                          </div>
                        )}
                      </div>
                      {selected && <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />}
                    </div>
                  </button>
                );
              })
            )}
          </div>
          )}

          {contrato && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg space-y-1" data-testid="contrato-preview">
              <p className="text-xs font-semibold text-emerald-800">Contrato selecionado:</p>
              <div className="text-sm"><b>{contrato.titulo}</b></div>
              <div className="text-xs text-slate-600"><b>Cliente:</b> {contrato.nome_cliente || "—"}</div>
              <div className="text-xs text-slate-600"><b>Endereço:</b> {contrato.endereco_obra || "—"}</div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Duração prevista (dias) *</Label>
              <Input
                type="number" min={1} max={1095}
                value={duracao}
                onChange={(e) => setDuracao(e.target.value)}
                placeholder="Ex: 90"
                data-testid="input-duracao-cronograma"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Data de início *</Label>
              <Input
                type="date"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                data-testid="input-data-inicio"
              />
            </div>
          </div>

          {dataConclusao && (
            <div className="text-xs text-slate-600 bg-slate-50 p-2 rounded">
              <b>Data de conclusão prevista:</b> {fmtDate(dataConclusao)} (calculada)
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={handleCreate}
            disabled={creating || !contrato}
            className="bg-luvcorp-navy hover:bg-luvcorp-navy/90 text-white"
            data-testid="btn-criar-cronograma"
          >
            {creating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
            Criar Cronograma
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== DASHBOARD (selected cronograma) ====================
function CronogramaDashboard({ data, onClose, onRefresh, onDelete, headers }) {
  const { cronograma, contrato, etapas, imprevistos, indicadores } = data;
  const [confirmFinalize, setConfirmFinalize] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(cronograma?.updated_at);

  // Update lastSavedAt whenever cronograma.updated_at changes (after refresh)
  useEffect(() => {
    if (cronograma?.updated_at) setLastSavedAt(cronograma.updated_at);
  }, [cronograma?.updated_at]);

  const playFinalizeSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      // Sequência ascendente "ding ding ding" de conclusão
      [523, 659, 784].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = freq;
        osc.type = "sine";
        gain.gain.value = 0;
        osc.connect(gain);
        gain.connect(ctx.destination);
        const start = ctx.currentTime + i * 0.18;
        gain.gain.linearRampToValueAtTime(0.18, start + 0.02);
        gain.gain.linearRampToValueAtTime(0, start + 0.18);
        osc.start(start);
        osc.stop(start + 0.2);
      });
    } catch (e) {
      // Audio context não disponível em alguns navegadores — fallback silencioso
      console.debug('[Cronograma] Audio chime not supported:', e?.message || e);
    }
  };

  const handleFinalizar = async () => {
    setFinalizing(true);
    try {
      await axios.post(`${API}/cronogramas/${cronograma.id}/finalizar`, {}, { headers });
      playFinalizeSound();
      toast.success("🏆 Obra finalizada com sucesso!", { duration: 4500 });
      setConfirmFinalize(false);
      onRefresh();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao finalizar"));
    } finally {
      setFinalizing(false);
    }
  };

  const totalEtapas = etapas.length;
  const concluidas = etapas.filter(e => e.status === "concluido").length;
  const todasConcluidas = totalEtapas > 0 && concluidas === totalEtapas;

  return (
    <TooltipProvider delayDuration={200}>
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <button onClick={onClose} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-1" data-testid="btn-voltar-lista">
            <X className="h-3 w-3" /> Voltar à lista
          </button>
          <h1 className="text-2xl font-bold text-luvcorp-navy" data-testid="dashboard-cronograma-title">
            {contrato?.titulo || "Cronograma"}
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Cliente: <b>{contrato?.cliente_nome || "—"}</b> · Endereço: {contrato?.endereco_obra || "—"}
          </p>
          {/* Auto-saved indicator */}
          <div className="flex items-center gap-2 mt-1.5">
            <Badge variant="outline" className="text-[10px] border-emerald-200 bg-emerald-50 text-emerald-700" data-testid="badge-auto-saved">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Salvo automaticamente · {lastSavedAt ? new Date(lastSavedAt).toLocaleString("pt-BR") : "—"}
            </Badge>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={onRefresh}
                  className="text-[10px] text-slate-500 hover:text-luvcorp-navy flex items-center gap-1 underline decoration-dotted"
                  data-testid="btn-salvar-cronograma"
                >
                  <Save className="h-3 w-3" /> Salvar agora
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs max-w-xs">
                  O cronograma é salvo automaticamente a cada alteração. Cada modificação fica registrada
                  no painel de <b>Auditoria</b> ao lado com data, usuário e valores antes/depois.
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <PDFCompletoButton data={data} />
          <ReplanejarButton cronogramaId={cronograma.id} headers={headers} onDone={onRefresh} disabled={cronograma.status === "finalizado"} />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={cronograma.status === "finalizado" ? "outline" : "default"}
                size="sm"
                className={cronograma.status === "finalizado"
                  ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                  : "bg-emerald-600 hover:bg-emerald-700 text-white"}
                disabled={cronograma.status === "finalizado" || totalEtapas === 0}
                onClick={() => setConfirmFinalize(true)}
                data-testid="btn-finalizar-cronograma"
              >
                <CheckCircle2 className="h-4 w-4 mr-1" />
                {cronograma.status === "finalizado" ? "Obra Finalizada" : "Finalizar Obra"}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs max-w-xs">
                <b>Finalizar:</b> bloqueia edições, marca a obra como concluída e gera PDF oficial.
                {totalEtapas === 0 ? " Adicione etapas antes de finalizar." :
                  !todasConcluidas ? ` ${concluidas}/${totalEtapas} etapas concluídas.` :
                  " Todas as etapas estão concluídas — pronto para finalizar!"}
              </p>
            </TooltipContent>
          </Tooltip>
          <Button variant="outline" size="sm" onClick={onDelete} data-testid="btn-excluir-cronograma" className="text-red-600 hover:text-red-700">
            <Trash2 className="h-4 w-4 mr-1" /> Excluir
          </Button>
        </div>
      </div>

      <IndicadoresCards cronograma={cronograma} indicadores={indicadores} />

      {indicadores.alerta_desvio && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" data-testid="alerta-desvio">
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-red-700">Atenção: desvio acima de 10%</p>
            <p className="text-red-600 text-xs">
              {indicadores.dias_perdidos_total} dias perdidos representam {indicadores.desvio_pct}% do prazo previsto.
              Considere replanejar.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <EtapasSection
            cronogramaId={cronograma.id}
            cronograma={cronograma}
            contrato={contrato}
            etapas={etapas}
            headers={headers}
            onRefresh={onRefresh}
            onSuggestFinalize={() => setConfirmFinalize(true)}
            readOnly={cronograma.status === "finalizado"}
          />
        </div>
        <div className="space-y-6">
          <ImprevistosSection
            cronogramaId={cronograma.id}
            imprevistos={imprevistos}
            etapas={etapas}
            headers={headers}
            onRefresh={onRefresh}
            readOnly={cronograma.status === "finalizado"}
          />
          <AuditoriaSection cronogramaId={cronograma.id} headers={headers} defaultOpen={true} />
        </div>
      </div>

      {/* Confirmação Finalizar Obra */}
      <AlertDialog open={confirmFinalize} onOpenChange={(v) => !v && setConfirmFinalize(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-emerald-600" />
              A obra foi concluída?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>Tem certeza que deseja <b>finalizar a obra</b>?</p>
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
                  <p className="font-semibold mb-1">⚠ Ao finalizar:</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>O cronograma fica <b>bloqueado para edição</b></li>
                    <li>Nenhuma etapa, item ou imprevisto poderá ser alterado</li>
                    <li>Replanejamento fica desabilitado</li>
                    <li>Status visível como &ldquo;Obra Finalizada&rdquo; no portfólio</li>
                  </ul>
                  <p className="mt-2"><b>Etapas concluídas:</b> {concluidas} de {totalEtapas}{!todasConcluidas && " (ainda há etapas pendentes)"}</p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={finalizing}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleFinalizar}
              disabled={finalizing}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="btn-confirm-finalizar"
            >
              {finalizing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
              Sim, finalizar obra
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </TooltipProvider>
  );
}

function IndicadoresCards({ cronograma, indicadores }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Prazo Previsto</p>
          <p className="text-2xl font-bold text-luvcorp-navy mt-1">{cronograma.duracao_prevista_dias}<span className="text-sm font-normal text-slate-400 ml-1">dias</span></p>
          <p className="text-xs text-slate-500 mt-1">{fmtDate(cronograma.data_inicio)} → {fmtDate(cronograma.data_conclusao_prevista)}</p>
        </CardContent>
      </Card>
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Progresso Físico</p>
          <p className="text-2xl font-bold text-emerald-600 mt-1" data-testid="indic-progresso">{indicadores.pct_progresso_fisico}%</p>
          <div className="h-1.5 bg-slate-100 rounded-full mt-2 overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${indicadores.pct_progresso_fisico}%` }} />
          </div>
          <p className="text-xs text-slate-500 mt-1">{indicadores.itens_concluidos}/{indicadores.total_itens} itens</p>
        </CardContent>
      </Card>
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Dias Perdidos</p>
          <p className={`text-2xl font-bold mt-1 ${indicadores.dias_perdidos_total > 0 ? "text-red-600" : "text-slate-700"}`} data-testid="indic-dias-perdidos">
            {indicadores.dias_perdidos_total}
          </p>
          <p className="text-xs text-slate-500 mt-1">Desvio: {indicadores.desvio_pct}%</p>
        </CardContent>
      </Card>
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Conclusão Projetada</p>
          <p className="text-2xl font-bold text-luvcorp-navy mt-1" data-testid="indic-conclusao-projetada">{fmtDate(indicadores.data_conclusao_projetada)}</p>
          <p className="text-xs text-slate-500 mt-1">Total etapas: {indicadores.duracao_etapas_total}d</p>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== ETAPAS SECTION ====================
function EtapasSection({ cronogramaId, cronograma, contrato, etapas, headers, onRefresh, onSuggestFinalize, readOnly }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editEtapa, setEditEtapa] = useState(null);
  const [confirmDeleteEtapa, setConfirmDeleteEtapa] = useState(null);

  const handleDeleteEtapa = async () => {
    if (!confirmDeleteEtapa) return;
    try {
      await axios.delete(`${API}/etapas/${confirmDeleteEtapa.id}`, { headers });
      toast.success("Etapa excluída");
      setConfirmDeleteEtapa(null);
      onRefresh();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao excluir etapa"));
    }
  };

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base text-luvcorp-navy flex items-center gap-2">
            <Calendar className="h-5 w-5" /> Etapas da Obra
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">{etapas.length} etapa{etapas.length !== 1 ? "s" : ""} • caminho crítico em vermelho</p>
        </div>
        {!readOnly && (
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="btn-add-etapa">
            <Plus className="h-4 w-4 mr-1" /> Etapa
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {etapas.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6">Nenhuma etapa adicionada ainda.</p>
        ) : (
          etapas.map((e) => (
            <EtapaRow
              key={e.id}
              etapa={e}
              etapas={etapas}
              contrato={contrato}
              cronograma={cronograma}
              headers={headers}
              onEdit={() => setEditEtapa(e)}
              onDelete={() => setConfirmDeleteEtapa(e)}
              onRefresh={onRefresh}
              onSuggestFinalize={onSuggestFinalize}
              readOnly={readOnly}
            />
          ))
        )}
      </CardContent>

      {/* Gantt Visual */}
      {etapas.length > 0 && <GanttChart etapas={etapas} />}

      <EtapaFormDialog
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onSaved={() => { setShowAdd(false); onRefresh(); }}
        cronogramaId={cronogramaId}
        etapas={etapas}
        headers={headers}
      />

      <EtapaFormDialog
        open={!!editEtapa}
        onClose={() => setEditEtapa(null)}
        onSaved={() => { setEditEtapa(null); onRefresh(); }}
        cronogramaId={cronogramaId}
        etapas={etapas}
        headers={headers}
        editing={editEtapa}
      />

      <AlertDialog open={!!confirmDeleteEtapa} onOpenChange={(v) => !v && setConfirmDeleteEtapa(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir etapa &ldquo;{confirmDeleteEtapa?.titulo}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>Tem certeza? Itens associados também serão excluídos.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteEtapa} className="bg-red-600 hover:bg-red-700 text-white">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function EtapaRow({ etapa, etapas, contrato, cronograma, headers, onEdit, onDelete, onRefresh, onSuggestFinalize, readOnly }) {
  const [expanded, setExpanded] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const pct = etapa.total_itens > 0 ? Math.round((etapa.itens_concluidos / etapa.total_itens) * 100) : 0;
  const dep = etapas.find(e => e.id === etapa.etapa_dependente_id);

  const handlePdfEtapa = async () => {
    setPdfLoading(true);
    const toastId = toast.loading("Gerando PDF da etapa...");
    try {
      const { blob, filename } = await gerarPDFEtapa({ etapa, etapas, contrato, cronograma });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success("PDF baixado", { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error("Erro ao gerar PDF", { id: toastId });
    } finally {
      setPdfLoading(false);
    }
  };

  const handleQuickConcluir = async () => {
    setStatusLoading(true);
    try {
      await axios.put(`${API}/etapas/${etapa.id}`, { status: "concluido" }, { headers });
      toast.success(`Etapa "${etapa.titulo}" marcada como concluída ✓`);
      await onRefresh();
      // Sugere auto-finalização se for a última pendente
      const restantes = etapas.filter(e => e.id !== etapa.id && e.status !== "concluido");
      if (restantes.length === 0 && onSuggestFinalize) {
        setTimeout(() => {
          toast("🎉 Todas as etapas estão concluídas! Deseja finalizar a obra?", {
            duration: 8000,
            action: { label: "Finalizar Obra", onClick: () => onSuggestFinalize() },
          });
        }, 600);
      }
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao concluir etapa"));
    } finally {
      setStatusLoading(false);
    }
  };

  const handleDuplicar = async () => {
    setDuplicating(true);
    try {
      await axios.post(`${API}/etapas/${etapa.id}/duplicar`, {}, { headers });
      toast.success(`Etapa duplicada: "${etapa.titulo} (cópia)"`);
      onRefresh();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao duplicar etapa"));
    } finally {
      setDuplicating(false);
    }
  };

  return (
    <div className={`border rounded-lg p-3 ${etapa.critico ? "border-red-300 bg-red-50/30" : "border-slate-200"}`} data-testid={`etapa-${etapa.id}`}>
      <div className="flex items-start gap-3">
        <button onClick={() => setExpanded(!expanded)} className="mt-1 text-slate-400 hover:text-slate-700">
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-400 font-mono">#{etapa.ordem}</span>
            <span className="font-semibold text-slate-800 truncate">{etapa.titulo}</span>
            <Badge className={`text-[10px] ${STATUS_ETAPA_COLOR[etapa.status]}`} variant="outline">
              {STATUS_LABEL[etapa.status]}
            </Badge>
            {etapa.critico && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="text-[10px] border-red-300 bg-red-100 text-red-700 cursor-help">
                    <AlertTriangle className="h-3 w-3 mr-0.5" /> Crítica
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs max-w-xs">
                    <b>Caminho Crítico:</b> folga = 0 dias. Se esta etapa atrasar,
                    a obra inteira atrasa. Priorize a execução.
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
            <span className="text-xs text-slate-500 ml-auto">{etapa.duracao_estimada_dias}d</span>
          </div>
          <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
            <span>{fmtDate(etapa.data_inicio_calculada)} → {fmtDate(etapa.data_fim_calculada)}</span>
            {dep && <span>↳ depende de &ldquo;{dep.titulo}&rdquo;</span>}
            <span>Folga: {etapa.folga}d</span>
          </div>
          {etapa.total_itens > 0 && (
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
              </div>
              <span className="text-[10px] text-slate-500 shrink-0">{etapa.itens_concluidos}/{etapa.total_itens}</span>
            </div>
          )}
        </div>
        {!readOnly && (
          <div className="flex gap-1 shrink-0">
            {etapa.status !== "concluido" && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                    onClick={handleQuickConcluir}
                    disabled={statusLoading}
                    data-testid={`btn-concluir-etapa-${etapa.id}`}
                  >
                    {statusLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent><p className="text-xs">Marcar como Concluído</p></TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleDuplicar} disabled={duplicating} data-testid={`btn-duplicar-etapa-${etapa.id}`}>
                  {duplicating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent><p className="text-xs">Duplicar etapa</p></TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handlePdfEtapa} disabled={pdfLoading} data-testid={`btn-pdf-etapa-${etapa.id}`}>
                  {pdfLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent><p className="text-xs">Gerar PDF desta etapa</p></TooltipContent>
            </Tooltip>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onEdit} data-testid={`btn-edit-etapa-${etapa.id}`}>
              <Edit className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600 hover:text-red-700" onClick={onDelete} data-testid={`btn-delete-etapa-${etapa.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
        {readOnly && (
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handlePdfEtapa} disabled={pdfLoading} title="Gerar PDF desta etapa" data-testid={`btn-pdf-etapa-${etapa.id}`}>
            {pdfLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
          </Button>
        )}
      </div>

      {expanded && (
        <div className="mt-3 pl-7 space-y-2 border-l-2 border-slate-100">
          {etapa.escopo && (
            <p className="text-xs text-slate-600 italic">{etapa.escopo}</p>
          )}
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Itens de Serviço</p>
            {!readOnly && (
              <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => setShowItemForm(true)} data-testid={`btn-add-item-${etapa.id}`}>
                <Plus className="h-3 w-3 mr-0.5" /> Item
              </Button>
            )}
          </div>
          {etapa.itens.length === 0 ? (
            <p className="text-xs text-slate-400 italic">Nenhum item de serviço.</p>
          ) : (
            <div className="space-y-1.5">
              {etapa.itens.map((it) => (
                <ItemRow key={it.id} item={it} headers={headers} onRefresh={onRefresh} readOnly={readOnly} />
              ))}
            </div>
          )}
        </div>
      )}

      <ItemFormDialog
        open={showItemForm}
        onClose={() => setShowItemForm(false)}
        onSaved={() => { setShowItemForm(false); onRefresh(); }}
        etapaId={etapa.id}
        headers={headers}
      />
    </div>
  );
}

function ItemRow({ item, headers, onRefresh, readOnly }) {
  const [updating, setUpdating] = useState(false);

  const changeStatus = async (newStatus) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/itens/${item.id}`, { status: newStatus }, { headers });
      toast.success("Status atualizado");
      onRefresh();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao atualizar"));
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Excluir item "${item.nome_servico}"?`)) return;
    try {
      await axios.delete(`${API}/itens/${item.id}`, { headers });
      toast.success("Item excluído");
      onRefresh();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao excluir"));
    }
  };

  return (
    <div className="flex items-center gap-2 text-xs bg-slate-50 rounded px-2 py-1.5" data-testid={`item-${item.id}`}>
      <NativeSelect
        value={item.status}
        onValueChange={(v) => changeStatus(v)}
        options={STATUS_ITEM_OPTS}
        className="h-7 text-xs w-32 shrink-0"
        disabled={readOnly || updating}
      />
      <div className="flex-1 min-w-0">
        <p className="truncate font-medium text-slate-700">{item.nome_servico}</p>
        {item.observacao && <p className="text-[10px] text-slate-500 truncate">{item.observacao}</p>}
      </div>
      {!readOnly && (
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-600 hover:text-red-700" onClick={handleDelete}>
          <Trash2 className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}

// ==================== ETAPA FORM DIALOG ====================
function EtapaFormDialog({ open, onClose, onSaved, cronogramaId, etapas, headers, editing }) {
  const [form, setForm] = useState({ titulo: "", escopo: "", duracao_estimada_dias: "", etapa_dependente_id: "", status: "pendente" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        titulo: editing.titulo || "",
        escopo: editing.escopo || "",
        duracao_estimada_dias: editing.duracao_estimada_dias != null ? String(editing.duracao_estimada_dias) : "",
        etapa_dependente_id: editing.etapa_dependente_id || "",
        status: editing.status || "pendente",
      });
    } else {
      setForm({ titulo: "", escopo: "", duracao_estimada_dias: "", etapa_dependente_id: "", status: "pendente" });
    }
  }, [editing, open]);

  const handleSave = async () => {
    if (!form.titulo.trim()) { toast.error("Título obrigatório"); return; }
    if (!form.duracao_estimada_dias || parseInt(form.duracao_estimada_dias, 10) < 1) {
      toast.error("Duração mínima: 1 dia"); return;
    }
    setSaving(true);
    try {
      const payload = {
        titulo: form.titulo.trim(),
        escopo: form.escopo || null,
        duracao_estimada_dias: parseInt(form.duracao_estimada_dias, 10),
        etapa_dependente_id: form.etapa_dependente_id || null,
      };
      if (editing) {
        payload.status = form.status;
        await axios.put(`${API}/etapas/${editing.id}`, payload, { headers });
        toast.success("Etapa atualizada");
      } else {
        await axios.post(`${API}/cronogramas/${cronogramaId}/etapas`, payload, { headers });
        toast.success("Etapa criada");
      }
      onSaved();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao salvar etapa"));
    } finally {
      setSaving(false);
    }
  };

  const depOpts = [
    { value: "", label: "Sem dependência" },
    ...etapas.filter(e => !editing || e.id !== editing.id).map(e => ({ value: e.id, label: `${e.ordem}. ${e.titulo}` })),
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="dialog-etapa">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar Etapa" : "Nova Etapa"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Título * <span className="text-slate-400">(max 100 chars)</span></Label>
            <Input value={form.titulo} maxLength={100} onChange={e => setForm(p => ({ ...p, titulo: e.target.value }))} data-testid="input-etapa-titulo" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Escopo/Descrição <span className="text-slate-400">(max 2000 chars)</span></Label>
            <Textarea value={form.escopo} maxLength={2000} rows={3} onChange={e => setForm(p => ({ ...p, escopo: e.target.value }))} data-testid="input-etapa-escopo" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Duração (dias) *</Label>
              <Input
                type="number" min={1}
                value={form.duracao_estimada_dias}
                onChange={e => setForm(p => ({ ...p, duracao_estimada_dias: e.target.value }))}
                data-testid="input-etapa-duracao"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1">Dependência
                <span className="text-slate-400 text-[10px]" title="Etapa que precisa ser concluída antes desta iniciar.">ⓘ</span>
              </Label>
              <NativeSelect
                value={form.etapa_dependente_id}
                onValueChange={(v) => setForm(p => ({ ...p, etapa_dependente_id: v }))}
                options={depOpts}
                data-testid="select-etapa-dependencia"
              />
            </div>
          </div>
          {editing && (
            <div className="space-y-1.5">
              <Label className="text-xs">Status</Label>
              <NativeSelect
                value={form.status}
                onValueChange={(v) => setForm(p => ({ ...p, status: v }))}
                options={STATUS_ITEM_OPTS}
                data-testid="select-etapa-status"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-luvcorp-navy hover:bg-luvcorp-navy/90 text-white" data-testid="btn-save-etapa">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            {editing ? "Salvar" : "Adicionar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ItemFormDialog({ open, onClose, onSaved, etapaId, headers }) {
  const [form, setForm] = useState({ nome_servico: "", status: "pendente", observacao: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm({ nome_servico: "", status: "pendente", observacao: "" }); }, [open]);

  const handleSave = async () => {
    if (!form.nome_servico.trim()) { toast.error("Nome do serviço obrigatório"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/etapas/${etapaId}/itens`, {
        nome_servico: form.nome_servico.trim(),
        status: form.status,
        observacao: form.observacao || null,
      }, { headers });
      toast.success("Item adicionado");
      onSaved();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao adicionar item"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="dialog-item">
        <DialogHeader>
          <DialogTitle>Novo Item de Serviço</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Nome do serviço *</Label>
            <Input value={form.nome_servico} maxLength={200} onChange={e => setForm(p => ({ ...p, nome_servico: e.target.value }))} data-testid="input-item-nome" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Status</Label>
            <NativeSelect value={form.status} onValueChange={(v) => setForm(p => ({ ...p, status: v }))} options={STATUS_ITEM_OPTS} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Observação <span className="text-slate-400">(max 500)</span></Label>
            <Textarea value={form.observacao} maxLength={500} rows={2} onChange={e => setForm(p => ({ ...p, observacao: e.target.value }))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-luvcorp-navy hover:bg-luvcorp-navy/90 text-white" data-testid="btn-save-item">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Adicionar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== GANTT CHART ====================
function GanttChart({ etapas }) {
  if (etapas.length === 0) return null;
  const maxDay = Math.max(...etapas.map(e => e.ef || 0), 1);
  const dayWidth = 100 / maxDay;

  return (
    <CardContent className="border-t pt-4 mt-2">
      <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3">Linha do Tempo (Gantt)</p>
      <div className="space-y-1.5" data-testid="gantt-chart">
        {etapas.map((e) => {
          const left = (e.es || 0) * dayWidth;
          const width = (e.duracao_estimada_dias || 1) * dayWidth;
          const color =
            e.status === "concluido" ? "bg-emerald-500" :
            e.status === "em_andamento" ? "bg-blue-500" :
            e.status === "atrasado" ? "bg-red-500" :
            e.critico ? "bg-red-400" : "bg-amber-400";
          // Progresso interno (% de itens concluídos) ou 100% se a etapa estiver concluída
          const pctItens = e.total_itens > 0 ? (e.itens_concluidos / e.total_itens) * 100 : 0;
          const pctProgresso = e.status === "concluido" ? 100 : pctItens;
          return (
            <div key={e.id} className="flex items-center gap-2 text-xs">
              <div className="w-28 truncate text-slate-700 shrink-0" title={e.titulo}>{e.titulo}</div>
              <div className="flex-1 relative h-5 bg-slate-100 rounded">
                <div
                  className={`absolute h-full rounded ${color} opacity-30 ${e.critico ? "ring-2 ring-red-600 ring-offset-1" : ""}`}
                  style={{ left: `${left}%`, width: `${Math.max(width, 1)}%` }}
                  title={`${e.titulo} (${fmtDate(e.data_inicio_calculada)} → ${fmtDate(e.data_fim_calculada)})`}
                />
                {/* Progresso preenchido */}
                {pctProgresso > 0 && (
                  <div
                    className={`absolute h-full rounded-l ${color}`}
                    style={{ left: `${left}%`, width: `${Math.max(width * (pctProgresso / 100), 0.5)}%` }}
                  />
                )}
                {/* Percentual visível dentro da barra */}
                {width > 8 && (
                  <span
                    className="absolute top-0 h-full flex items-center justify-center text-[9px] font-bold text-white drop-shadow"
                    style={{ left: `${left}%`, width: `${Math.max(width, 1)}%` }}
                  >
                    {Math.round(pctProgresso)}%
                  </span>
                )}
              </div>
              <div className="w-12 text-right text-slate-500 shrink-0 font-mono text-[10px]">{e.duracao_estimada_dias}d</div>
            </div>
          );
        })}
        <div className="flex items-center gap-2 text-[10px] mt-3 pt-2 border-t">
          <div className="w-28" />
          <div className="flex-1 flex justify-between text-slate-400 font-mono">
            <span>Dia 0</span>
            <span>Dia {maxDay}</span>
          </div>
          <div className="w-12" />
        </div>
        <div className="flex items-center gap-3 text-[10px] text-slate-500 mt-2">
          <span className="flex items-center gap-1"><div className="h-2 w-2 bg-amber-400 rounded" /> Pendente</span>
          <span className="flex items-center gap-1"><div className="h-2 w-2 bg-blue-500 rounded" /> Em Andamento</span>
          <span className="flex items-center gap-1"><div className="h-2 w-2 bg-emerald-500 rounded" /> Concluído</span>
          <span className="flex items-center gap-1"><div className="h-2 w-2 bg-red-500 rounded" /> Atrasado</span>
          <span className="flex items-center gap-1"><div className="h-2 w-2 rounded ring-1 ring-red-600" /> Crítico</span>
        </div>
      </div>
    </CardContent>
  );
}

// ==================== AJUSTES DE PRAZO (Imprevistos/Antecipações) ====================
function ImprevistosSection({ cronogramaId, imprevistos, etapas, headers, onRefresh, readOnly }) {
  const [showForm, setShowForm] = useState(false);

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-base text-luvcorp-navy flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" /> Ajustes de Prazo
        </CardTitle>
        {!readOnly && (
          <Button size="sm" variant="outline" onClick={() => setShowForm(true)} data-testid="btn-add-imprevisto">
            <Plus className="h-4 w-4 mr-1" /> Registrar
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-2 max-h-80 overflow-y-auto">
        {imprevistos.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">
            Nenhum ajuste registrado. Use este painel para registrar <b>atrasos</b> (chuva, falta de material, etc) ou <b>antecipações</b> (etapa concluída antes do prazo).
          </p>
        ) : (
          imprevistos.map(i => {
            const etapa = etapas.find(e => e.id === i.etapa_id);
            const tipoLabel = TIPO_IMPREVISTO_OPTS.find(t => t.value === i.tipo)?.label || i.tipo;
            const isAntecipacao = i.tipo_ajuste === "antecipacao" || (i.dias_perdidos || 0) < 0;
            const dias = Math.abs(i.dias_perdidos || i.dias_brutos || 0);
            return (
              <div
                key={i.id}
                className={`border rounded p-2 text-xs space-y-1 ${isAntecipacao ? "border-emerald-200 bg-emerald-50" : "border-slate-200"}`}
                data-testid={`imprevisto-${i.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Badge variant="outline" className={`text-[10px] ${isAntecipacao ? "border-emerald-300 bg-emerald-100 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
                      {isAntecipacao ? "⚡ Antecipação" : "⚠ Atraso"}
                    </Badge>
                    <span className="text-slate-500 text-[10px]">{tipoLabel}</span>
                  </div>
                  <span className={`font-semibold ${isAntecipacao ? "text-emerald-700" : "text-red-600"}`}>
                    {isAntecipacao ? "+" : "-"}{dias}d
                  </span>
                </div>
                <p className="text-slate-600">{fmtDate(i.data_ocorrencia)}</p>
                {etapa && <p className="text-slate-500 truncate">Etapa: {etapa.titulo}</p>}
                {i.recalcular_cronograma === false && (
                  <p className="text-[10px] text-slate-400 italic">⚠ Não recalculado no cronograma</p>
                )}
                {i.descricao && <p className="text-slate-500 italic line-clamp-2">{i.descricao}</p>}
              </div>
            );
          })
        )}
      </CardContent>
      <ImprevistoFormDialog
        open={showForm}
        onClose={() => setShowForm(false)}
        onSaved={() => { setShowForm(false); onRefresh(); }}
        cronogramaId={cronogramaId}
        etapas={etapas}
        headers={headers}
      />
    </Card>
  );
}

function ImprevistoFormDialog({ open, onClose, onSaved, cronogramaId, etapas, headers }) {
  const [form, setForm] = useState({
    tipo_ajuste: "atraso",
    tipo: "",
    data_ocorrencia: new Date().toISOString().split("T")[0],
    dias_perdidos: "",
    etapa_id: "",
    descricao: "",
    recalcular_cronograma: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({
      tipo_ajuste: "atraso",
      tipo: "",
      data_ocorrencia: new Date().toISOString().split("T")[0],
      dias_perdidos: "",
      etapa_id: "",
      descricao: "",
      recalcular_cronograma: true,
    });
  }, [open]);

  const isAntecipacao = form.tipo_ajuste === "antecipacao";

  const handleSave = async () => {
    if (!form.tipo) { toast.error("Categoria obrigatória"); return; }
    if (!form.dias_perdidos || parseInt(form.dias_perdidos, 10) < 1) { toast.error("Dias: mínimo 1"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/cronogramas/${cronogramaId}/imprevistos`, {
        tipo: form.tipo,
        tipo_ajuste: form.tipo_ajuste,
        data_ocorrencia: form.data_ocorrencia,
        dias_perdidos: parseInt(form.dias_perdidos, 10),
        etapa_id: form.etapa_id || null,
        descricao: form.descricao || null,
        recalcular_cronograma: form.recalcular_cronograma,
      }, { headers });
      toast.success(isAntecipacao ? "Antecipação registrada ⚡" : "Atraso registrado");
      onSaved();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao registrar ajuste"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="dialog-imprevisto">
        <DialogHeader>
          <DialogTitle>Registrar Ajuste de Prazo</DialogTitle>
          <DialogDescription>
            Registre um <b>atraso</b> (imprevisto) ou uma <b>antecipação</b> (etapa concluída antes do previsto).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {/* Toggle Atraso / Antecipação */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setForm(p => ({ ...p, tipo_ajuste: "atraso" }))}
              className={`p-3 rounded-lg border-2 text-left transition-all ${
                !isAntecipacao
                  ? "border-amber-400 bg-amber-50"
                  : "border-slate-200 bg-white hover:border-slate-300"
              }`}
              data-testid="btn-tipo-atraso"
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className={`h-4 w-4 ${!isAntecipacao ? "text-amber-600" : "text-slate-400"}`} />
                <span className={`text-sm font-semibold ${!isAntecipacao ? "text-amber-900" : "text-slate-600"}`}>Atraso</span>
              </div>
              <p className="text-[10px] text-slate-500 mt-0.5">Dias perdidos</p>
            </button>
            <button
              type="button"
              onClick={() => setForm(p => ({ ...p, tipo_ajuste: "antecipacao" }))}
              className={`p-3 rounded-lg border-2 text-left transition-all ${
                isAntecipacao
                  ? "border-emerald-400 bg-emerald-50"
                  : "border-slate-200 bg-white hover:border-slate-300"
              }`}
              data-testid="btn-tipo-antecipacao"
            >
              <div className="flex items-center gap-2">
                <Sparkles className={`h-4 w-4 ${isAntecipacao ? "text-emerald-600" : "text-slate-400"}`} />
                <span className={`text-sm font-semibold ${isAntecipacao ? "text-emerald-900" : "text-slate-600"}`}>Antecipação</span>
              </div>
              <p className="text-[10px] text-slate-500 mt-0.5">Dias ganhos</p>
            </button>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Categoria *</Label>
            <NativeSelect
              value={form.tipo}
              onValueChange={(v) => setForm(p => ({ ...p, tipo: v }))}
              options={[
                { value: "", label: isAntecipacao ? "Selecione o motivo da antecipação..." : "Selecione o tipo..." },
                ...TIPO_IMPREVISTO_OPTS,
              ]}
              data-testid="select-imprevisto-tipo"
            />
            <p className="text-[10px] text-slate-500">
              {isAntecipacao
                ? "Use 'Outro' caso a antecipação se deva a aumento de equipe ou produtividade."
                : "Categorize a causa do atraso (chuva, falta de material, etc)."}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Data de Ocorrência *</Label>
              <Input
                type="date"
                value={form.data_ocorrencia}
                onChange={e => setForm(p => ({ ...p, data_ocorrencia: e.target.value }))}
                data-testid="input-imprevisto-data"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{isAntecipacao ? "Dias antecipados *" : "Dias perdidos *"}</Label>
              <Input
                type="number" min={1}
                value={form.dias_perdidos}
                onChange={e => setForm(p => ({ ...p, dias_perdidos: e.target.value }))}
                data-testid="input-imprevisto-dias"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Etapa Afetada (opcional)</Label>
            <NativeSelect
              value={form.etapa_id}
              onValueChange={(v) => setForm(p => ({ ...p, etapa_id: v }))}
              options={[
                { value: "", label: "Não vincular a etapa específica" },
                ...etapas.map(e => ({ value: e.id, label: `${e.ordem}. ${e.titulo}` })),
              ]}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Descrição <span className="text-slate-400">(max 1000)</span></Label>
            <Textarea
              value={form.descricao}
              maxLength={1000}
              rows={2}
              onChange={e => setForm(p => ({ ...p, descricao: e.target.value }))}
              placeholder={isAntecipacao
                ? "Ex: Cliente liberou pagamento adiantado e equipe extra reduziu prazo"
                : "Ex: Chuvas intensas paralisaram a obra por 3 dias"}
            />
          </div>

          {/* Checkbox Recalcular */}
          <label className="flex items-start gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200 cursor-pointer">
            <input
              type="checkbox"
              checked={form.recalcular_cronograma}
              onChange={(e) => setForm(p => ({ ...p, recalcular_cronograma: e.target.checked }))}
              className="mt-0.5 accent-luvcorp-navy"
              data-testid="checkbox-recalcular"
            />
            <div className="text-xs">
              <p className="font-semibold text-slate-800">Recalcular cronograma automaticamente</p>
              <p className="text-slate-500 mt-0.5">
                {isAntecipacao
                  ? "Marcar para deduzir os dias antecipados do desvio acumulado (encurta a previsão de conclusão)."
                  : "Marcar para somar os dias perdidos ao desvio acumulado (estende a previsão de conclusão)."}
                {" "}Desmarque se quiser apenas <b>registrar histórico</b> sem alterar as datas.
              </p>
            </div>
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className={isAntecipacao ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-luvcorp-navy hover:bg-luvcorp-navy/90 text-white"}
            data-testid="btn-save-imprevisto"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Registrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== PDF COMPLETO BUTTON ====================
function PDFCompletoButton({ data }) {
  const [loading, setLoading] = useState(false);
  const handleClick = async () => {
    setLoading(true);
    const toastId = toast.loading("Gerando PDF...");
    try {
      const { blob, filename } = await gerarPDFCompleto({
        cronograma: data.cronograma,
        contrato: data.contrato,
        etapas: data.etapas,
        imprevistos: data.imprevistos,
        indicadores: data.indicadores,
        planoAcao: null,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success("PDF baixado", { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error("Erro ao gerar PDF", { id: toastId });
    } finally {
      setLoading(false);
    }
  };
  return (
    <Button size="sm" onClick={handleClick} disabled={loading || (data.etapas || []).length === 0}
      variant="outline" data-testid="btn-pdf-completo"
      title={(data.etapas || []).length === 0 ? "Adicione etapas antes de gerar PDF" : "Gerar PDF completo do cronograma"}>
      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileDown className="h-4 w-4 mr-1" />}
      PDF Completo
    </Button>
  );
}

// ==================== REPLANEJAR BUTTON ====================
function ReplanejarButton({ cronogramaId, headers, onDone, disabled }) {
  const [loading, setLoading] = useState(false);
  const [plano, setPlano] = useState(null);

  const handleClick = async () => {
    setLoading(true);
    const toastId = toast.loading("Replanejando...");
    try {
      const res = await axios.post(`${API}/cronogramas/${cronogramaId}/replanejar`, {}, { headers });
      setPlano(res.data);
      toast.success("Replanejamento concluído", { id: toastId });
      onDone();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao replanejar"), { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button size="sm" onClick={handleClick} disabled={loading || disabled} variant="outline" data-testid="btn-replanejar"
        title={disabled ? "Cronograma finalizado não pode ser replanejado" : "Replanejar com base em desvios e CPM"}>
        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
        Replanejar
      </Button>
      <Dialog open={!!plano} onOpenChange={(v) => !v && setPlano(null)}>
        <DialogContent className="max-w-2xl" data-testid="dialog-plano-acao">
          <DialogHeader>
            <DialogTitle>Plano de Ação - Replanejamento</DialogTitle>
            <DialogDescription>Sugestões geradas por análise interna (CPM).</DialogDescription>
          </DialogHeader>
          {plano && (
            <div className="space-y-4 py-2 text-sm">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                <p className="font-semibold text-amber-800 text-xs uppercase mb-1">Resumo do Desvio</p>
                <p className="text-amber-900">{plano.plano_acao.resumo_desvio}</p>
              </div>
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded">
                <p className="font-semibold text-emerald-800 text-xs uppercase mb-1">Nova Conclusão Projetada</p>
                <p className="text-emerald-900 font-bold text-lg">{fmtDate(plano.data_conclusao_projetada)}</p>
              </div>
              <div>
                <p className="font-semibold text-slate-700 text-xs uppercase mb-1">Foco da Próxima Etapa</p>
                <p className="text-slate-600">{plano.plano_acao.focus_proxima_etapa}</p>
              </div>
              <div>
                <p className="font-semibold text-slate-700 text-xs uppercase mb-2">Recomendações</p>
                <ul className="space-y-1.5 text-slate-700">
                  {plano.plano_acao.recomendacoes.map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs"><span className="text-luvcorp-navy">•</span><span>{r}</span></li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ==================== AUDITORIA ====================
function AuditoriaSection({ cronogramaId, headers, defaultOpen = false }) {
  const [logs, setLogs] = useState([]);
  const [open, setOpen] = useState(defaultOpen);

  const fetchLogs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cronogramas/${cronogramaId}/auditoria?limit=50`, { headers });
      setLogs(res.data || []);
    } catch (err) {
      console.error(err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cronogramaId]);

  useEffect(() => { if (open) fetchLogs(); }, [open, fetchLogs]);

  return (
    <Card className="border-0 shadow-sm" data-testid="card-auditoria">
      <CardHeader className="pb-3 flex flex-row items-center justify-between cursor-pointer" onClick={() => setOpen(!open)}>
        <CardTitle className="text-base text-luvcorp-navy flex items-center gap-2">
          <History className="h-5 w-5" /> Histórico de Alterações
          {logs.length > 0 && (
            <Badge variant="outline" className="text-[10px] ml-1">{logs.length}</Badge>
          )}
        </CardTitle>
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </CardHeader>
      {open && (
        <CardContent className="max-h-72 overflow-y-auto space-y-1.5 text-xs">
          <p className="text-[10px] text-slate-400 italic mb-2">
            Todas as alterações são registradas automaticamente — usuário, data, valor antigo e novo.
          </p>
          {logs.length === 0 ? (
            <p className="text-slate-400 italic">Sem registros.</p>
          ) : (
            logs.map(l => (
              <div key={l.id} className="border-l-2 border-slate-200 pl-2 py-1" data-testid={`audit-${l.id}`}>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[9px]">{l.acao}</Badge>
                  <span className="text-slate-500">{l.usuario_nome || "—"}</span>
                  <span className="text-slate-400 text-[10px] ml-auto">{new Date(l.created_at).toLocaleString("pt-BR")}</span>
                </div>
                {l.campo_alterado && (
                  <p className="text-slate-600 mt-0.5">
                    <b>{l.campo_alterado}</b>
                    {l.valor_anterior && <span className="text-red-500 line-through ml-1">{l.valor_anterior}</span>}
                    {l.valor_novo && <span className="text-emerald-600 ml-1">→ {l.valor_novo}</span>}
                  </p>
                )}
                {l.descricao_extra && <p className="text-slate-500 italic mt-0.5">{l.descricao_extra}</p>}
              </div>
            ))
          )}
        </CardContent>
      )}
    </Card>
  );
}
